import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { isOneOfService, propsOf, publicExposure, arrProp } from './_graphHelpers';

const INGRESS_SERVICES = [
  'alb',
  'nlb',
  'load-balancer',
  'apigateway',
  'api-gateway',
  'cloudfront',
  'distribution',
] as const;

/**
 * P0.3 — public ingress without WAF.
 * Severity: critical. SOC 2 CC6.6 / CC7.2.
 *
 * Fires when an internet-facing ingress resource has no associated
 * WAF. Detection is conservative — if WAF association data isn't in
 * the scan, the rule stays quiet.
 */
export const publicWithoutWafRule: FindingRule = {
  id: 'public_without_waf',
  label: 'Public without WAF',
  description: 'Public ingress resource with no WAF attached.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      if (!isOneOfService(node, INGRESS_SERVICES)) continue;
      const exposure = publicExposure(node);
      if (exposure !== 'open' && exposure !== 'restricted') continue;
      const p = propsOf(node);
      const wafAttached =
        (arrProp(p, ['access', 'wafAssociated']) ?? []).length > 0 ||
        p.wafAclArn !== undefined;
      if (!wafAttached) {
        findings.push({
          id: `public_without_waf:resource:${node.id}`,
          ruleId: 'public_without_waf',
          severity: 'critical',
          scope: 'resource',
          subject: { kind: 'resource', id: node.id },
          title: 'Public without WAF',
          detail: `${String(node.label ?? node.id)} is internet-facing but has no WAF attached.`,
        });
      }
    }
    return findings;
  },
};
